import * as React from "react";
import { Objects } from "../../../../../../../react-core/Utils/Objects";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { DocumentTaskRequestTypeEnum } from "../../../../../../../Wrap/Constants/Enums/Tasks/DocumentTaskRequestTypeEnum";
import { EncryptedInstructionDocumentTypeRequest } from "../../../../../../../Wrap/Models/Models/Documents/InstructionDocuments/EncryptedInstructionDocumentTypeRequest";
import { ApplyModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { PensionContributionModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContributionModel";
import { GiaContributionModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/PersonalPortfolio/GiaContributionModel";
import { WrapCashContributionModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/WrapCash/WrapCashContributionModel";
import { IsaContributionModel } from "../../../../../../../Wrap/Models/Models/Processes/OneOffPayment/Isa/IsaContributionModel";
import { ContributionAndWithdrawalBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/ContributionAndWithdrawalBase";
import { MultiDownloadButton } from "../../../../../../Shared/DownloadButton/MultiDownloadButton";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { DocumentsHelper } from "../../../PortfolioDashboard/Components/Document/Resource/DocumentsHelper";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { processTypeEnum } from "../../Resource/Contents";
import { SectionHeader } from "../../SharedComponents";
import { InstructionDocumentTypeRequestTriggerActionsCreator } from "../Actions/InstructionDocumentTypeRequestTriggerActionsCreator";
import { IllustrationType } from "../Resource/Content";

interface IQuoteAndChargesProps extends IProcessComponentProps {
    data:
        | ApplyModel
        | GiaContributionModel
        | IsaContributionModel
        | PensionContributionModel
        | WrapCashContributionModel;
    processType: processTypeEnum;
    subAccountType: SubAccountType;
    needGoToDlu: boolean;
    selectAll: boolean;
    triggerred: number;
    clAccountId?: string;
    customerId?: number;
}

interface IQuoteAndCharges {}

export class QuoteAndCharges extends ProcessComponent<IQuoteAndChargesProps, IQuoteAndCharges> {
    constructor(props: IQuoteAndChargesProps) {
        super(props);
        this.state = {};
    }

    private illustrationTrigger(callback, fileName: string, illustrationType: IllustrationType) {
        if (
            this.props.processType == processTypeEnum.NewBusiness ||
            this.props.processType == processTypeEnum.NewBusinessExistingClient
        ) {
            const dataClone: ApplyModel = Objects.cloneObject(this.props.data);
            let applyModel: ApplyModel = {};
            if (illustrationType == IllustrationType.GiaIllustration) {
                applyModel.GiaModel = dataClone.GiaModel;
            } else if (illustrationType == IllustrationType.IsaIllustration) {
                applyModel.IsaModel = dataClone.IsaModel;
            } else if (illustrationType == IllustrationType.WrapCashIllustration) {
                applyModel.WrapCashModel = dataClone.WrapCashModel;
            } else {
                applyModel = dataClone;
            }

            InstructionDocumentTypeRequestTriggerActionsCreator.triggerByApplyModel(
                applyModel,
                (data: EncryptedInstructionDocumentTypeRequest[]) => {
                    const currentOne = data.find((item) => item?.DocumentType?.Name.includes(fileName));
                    callback(currentOne?.DocumentRequest?.Id, currentOne?.DocumentType?.Name + ".pdf");
                }
            );
        } else {
            InstructionDocumentTypeRequestTriggerActionsCreator.triggerByContributionAndWithdrawalBase(
                this.props.data,
                (data: EncryptedInstructionDocumentTypeRequest[]) => {
                    const currentOne = data.find((item) => item?.DocumentType?.Name.includes(fileName));
                    callback(currentOne?.DocumentRequest?.Id, currentOne?.DocumentType?.Name + ".pdf");
                }
            );
        }
    }

    private showIsaIllustrationCondition = (processType: processTypeEnum) => {
        if (processType == processTypeEnum.NewBusiness || processType == processTypeEnum.NewBusinessExistingClient) {
            return (this.props.data as ApplyModel).SelectedProducts.some((ele) =>
                SubAccountTypeHelper.isIsa(ele.SubAccountType)
            );
        }
        if (processType == processTypeEnum.SinglePaymentTransfer || processType == processTypeEnum.RegularPayment) {
            return this.props.subAccountType == SubAccountType.StocksIsa;
        }
        return false;
    };

    private showPPIllustrationCondition = (processType: processTypeEnum) => {
        if (processType == processTypeEnum.NewBusiness || processType == processTypeEnum.NewBusinessExistingClient) {
            return (this.props.data as ApplyModel).SelectedProducts.some((ele) =>
                SubAccountTypeHelper.isPP(ele.SubAccountType)
            );
        }
        if (processType == processTypeEnum.SinglePaymentTransfer || processType == processTypeEnum.RegularPayment) {
            return SubAccountTypeHelper.isPP(this.props.subAccountType);
        }
        return false;
    };

    private hasAllocationExceptGBPCash(model: ContributionAndWithdrawalBase): boolean {
        return model?.InstrumentSelections?.Instruments?.some((x) => x.Instrument.Name != "GBP Cash");
    }

    private displayChargesInformation(): boolean {
        const { data, processType } = this.props;

        if (processType == processTypeEnum.SinglePaymentTransfer || processType == processTypeEnum.RegularPayment) {
            return this.hasAllocationExceptGBPCash(
                (data as
                    | GiaContributionModel
                    | IsaContributionModel
                    | PensionContributionModel
                    | WrapCashContributionModel)?.Contribution
            );
        } else if (
            processType == processTypeEnum.NewBusiness ||
            processType == processTypeEnum.NewBusinessExistingClient
        ) {
            return (
                this.hasAllocationExceptGBPCash((data as ApplyModel)?.GiaModel) ||
                this.hasAllocationExceptGBPCash((data as ApplyModel)?.IsaModel) ||
                this.hasAllocationExceptGBPCash((data as ApplyModel)?.PensionApply)
            );
        }
    }

    private displayChargesSummary(): boolean {
        const { processType } = this.props;
        if (this.props.needGoToDlu) {
            return false;
        }

        if (
            processType == processTypeEnum.NewBusiness ||
            processType == processTypeEnum.NewBusinessExistingClient ||
            processType == processTypeEnum.SinglePaymentTransfer ||
            processType == processTypeEnum.RegularPayment
        ) {
            return true;
        }
        return false;
    }

    render() {
        const { processType } = this.props;
        const props = {
            triggerred: this.props.triggerred,
            isSelectedAll: this.props.selectAll,
        };
        const params = getQueryParameters();
        const customerid = params["customerid"] ?? this.props.customerId;
        const claccountid = this.props.clAccountId;
        const instructionId = params["id"];
        const headAccountHierarchyId = params["hierarchyid"];
        return (
            <>
                {SectionHeader(
                    "Quotes and charges",
                    "These documents will also appear in your client’s document store."
                )}
                {this.showIsaIllustrationCondition(processType) && (
                    <MultiDownloadButton
                        fileName="ISA Illustration"
                        triggerAuto
                        {...props}
                        data={null}
                        onChange={() => {}}
                        onRef={(ref) => this.appendChild(ref)}
                        request={DocumentsHelper.getTaskRequest(
                            instructionId,
                            customerid,
                            claccountid,
                            headAccountHierarchyId,
                            DocumentTaskRequestTypeEnum.ChargesInformation,
                            processType
                        )}
                        selectedAccount={headAccountHierarchyId}
                        trigger={(callback) => {
                            this.illustrationTrigger(callback, "ISA Illustration", IllustrationType.IsaIllustration);
                        }}
                    />
                )}
                {this.showPPIllustrationCondition(processType) && (
                    <MultiDownloadButton
                        fileName="Personal Portfolio illustration"
                        triggerAuto
                        {...props}
                        data={null}
                        onChange={() => {}}
                        onRef={(ref) => this.appendChild(ref)}
                        request={DocumentsHelper.getTaskRequest(
                            instructionId,
                            customerid,
                            claccountid,
                            headAccountHierarchyId,
                            DocumentTaskRequestTypeEnum.ChargesInformation,
                            processType
                        )}
                        selectedAccount={headAccountHierarchyId}
                        trigger={(callback) => {
                            this.illustrationTrigger(
                                callback,
                                "Personal Portfolio illustration",
                                IllustrationType.GiaIllustration
                            );
                        }}
                    />
                )}
                {this.displayChargesInformation() && (
                    <MultiDownloadButton
                        fileName="Charges Information"
                        {...props}
                        data={null}
                        onChange={() => {}}
                        onRef={(ref) => this.appendChild(ref)}
                        request={DocumentsHelper.getTaskRequest(
                            instructionId,
                            customerid,
                            claccountid,
                            headAccountHierarchyId,
                            DocumentTaskRequestTypeEnum.ChargesInformation,
                            processType
                        )}
                        triggerAuto
                        selectedAccount={headAccountHierarchyId}
                    />
                )}
                {this.displayChargesSummary() && (
                    <MultiDownloadButton
                        fileName="Charges Summary"
                        triggerAuto
                        {...props}
                        data={null}
                        onChange={() => {}}
                        onRef={(ref) => this.appendChild(ref)}
                        request={DocumentsHelper.getTaskRequest(
                            instructionId,
                            customerid,
                            claccountid,
                            headAccountHierarchyId,
                            DocumentTaskRequestTypeEnum.ChargeSummary,
                            processType
                        )}
                        selectedAccount={headAccountHierarchyId}
                    />
                )}
            </>
        );
    }
}
